import { db, delay } from "./api";

export const procurementService = {
  async getSuppliers() {
    await delay(200);
    return [...db.suppliers];
  },

  async createSupplier(data) {
    await delay(300);
    const newSupplier = {
      ...data,
      id: `SUP-${String(db.suppliers.length + 1).padStart(3, "0")}`,
      outstandingBalance: Number(data.outstandingBalance) || 0,
      totalPurchasesYTD: 0,
      performanceRating: 5.0,
      onTimeDeliveryRate: 100
    };
    db.suppliers.unshift(newSupplier);
    db.persistAll();
    return newSupplier;
  },

  async updateSupplier(id, updates) {
    await delay(250);
    const idx = db.suppliers.findIndex((s) => s.id === id);
    if (idx !== -1) {
      db.suppliers[idx] = { ...db.suppliers[idx], ...updates };
      db.persistAll();
      return db.suppliers[idx];
    }
    throw new Error("Supplier not found");
  },

  async deleteSupplier(id) {
    await delay(250);
    const idx = db.suppliers.findIndex((s) => s.id === id);
    if (idx !== -1) {
      db.suppliers.splice(idx, 1);
      db.persistAll();
      return true;
    }
    return false;
  },

  async getPurchaseOrders() {
    await delay(250);
    return [...db.purchaseOrders];
  },

  async createPurchaseOrder(data) {
    await delay(350);
    const totalAmount = data.items.reduce((sum, item) => sum + (item.total || item.orderedQty * item.unitPrice), 0);
    const landedCost = totalAmount + Number(data.freightCharges || 0) + Number(data.packagingCharges || 0) + Number(data.handlingCharges || 0);

    const newPO = {
      ...data,
      id: `PO-${Date.now()}`,
      poNumber: `PO/2026/08/${Math.floor(100 + Math.random() * 900)}`,
      status: data.status || "Sent",
      orderDate: data.orderDate || new Date().toISOString().slice(0, 10),
      totalAmount,
      landedCost
    };

    // Update supplier outstanding balance
    const supplier = db.suppliers.find((s) => s.id === data.supplierId);
    if (supplier) {
      supplier.outstandingBalance += landedCost;
      supplier.totalPurchasesYTD = (supplier.totalPurchasesYTD || 0) + landedCost;
    }

    db.purchaseOrders.unshift(newPO);

    db.auditLogs.unshift({
      id: `AUD-${Date.now()}`,
      user: "Procurement Officer",
      role: "Procurement",
      action: "Created Purchase Order",
      module: "Procurement",
      timestamp: new Date().toISOString().replace("T", " ").slice(0, 19),
      details: `Generated ${newPO.poNumber} for ${supplier?.name || "Supplier"} amounting to ₹${landedCost.toLocaleString("en-IN")}`
    });

    db.persistAll();
    return newPO;
  },

  async updatePOStatus(poId, newStatus) {
    await delay(250);
    const po = db.purchaseOrders.find((p) => p.id === poId);
    if (po) {
      po.status = newStatus;
      db.persistAll();
      return po;
    }
    throw new Error("PO not found");
  },

  async receiveGRN(grnData) {
    await delay(450);
    const newGRN = {
      ...grnData,
      id: `GRN-${Date.now()}`,
      grnNumber: `GRN/2026/08/${Math.floor(100 + Math.random() * 900)}`,
      receivedDate: grnData.receivedDate || new Date().toISOString().slice(0, 10)
    };

    // Update PO status & received quantities
    const po = db.purchaseOrders.find((p) => p.id === grnData.poId);
    if (po) {
      let allReceived = true;
      grnData.items.forEach((grnItem) => {
        const poItem = po.items.find((i) => i.productId === grnItem.productId);
        if (poItem) {
          poItem.receivedQty = (poItem.receivedQty || 0) + Number(grnItem.receivedQty || 0);
          if (poItem.receivedQty < poItem.orderedQty) {
            allReceived = false;
          }
        }
      });
      po.status = allReceived ? "Closed" : "Partially Received";
    }

    // Add or increment batches & bin counts
    grnData.items.forEach((item) => {
      const acceptedQty = Number(item.receivedQty || 0) - Number(item.damagedQty || 0) - Number(item.rejectedQty || 0);
      if (acceptedQty > 0) {
        const existingBatch = db.batches.find((b) => b.batchNo === item.batchNo && b.warehouseId === grnData.warehouseId);
        if (existingBatch) {
          existingBatch.qtyRemaining += acceptedQty;
        } else {
          db.batches.unshift({
            id: `BAT-${Date.now()}-${Math.floor(Math.random() * 100)}`,
            batchNo: item.batchNo || `BAT-${Date.now().toString().slice(-4)}`,
            productId: item.productId,
            productName: item.productName,
            mfgDate: item.mfgDate || "2026-08-01",
            expiryDate: item.expiryDate || "2027-08-01",
            qtyRemaining: acceptedQty,
            warehouseId: grnData.warehouseId,
            warehouseCode: grnData.warehouseId === "WH-001" ? "WH-BHIWANDI" : grnData.warehouseId === "WH-002" ? "WH-KURLA" : "WH-DADAR",
            binCode: item.destinationBin || "A1-S1-B1",
            unitCost: item.unitPrice || 100,
            mrp: item.mrp || 150,
            status: "Good"
          });
        }
      }
    });

    db.grns.unshift(newGRN);

    db.auditLogs.unshift({
      id: `AUD-${Date.now()}`,
      user: grnData.inspector || "Warehouse Receiver",
      role: "Warehouse Staff",
      action: "Received GRN Putaway",
      module: "Receiving / GRN",
      timestamp: new Date().toISOString().replace("T", " ").slice(0, 19),
      details: `Processed GRN ${newGRN.grnNumber} against ${po?.poNumber || "PO"}`
    });

    db.persistAll();
    return newGRN;
  },

  async getGRNs() {
    await delay(200);
    return [...db.grns];
  }
};
